import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Users } from "lucide-react";
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";

const LINE_COLORS = ["#FACC15", "#22C55E", "#3B82F6", "#EC4899", "#FB923C", "#A78BFA", "#2DD4BF", "#F87171"];
const day = (iso) => (iso ? new Date(iso).toLocaleDateString("tr-TR", { day: "2-digit", month: "short" }) : "");
const tip = { background: "#0d1117", border: "1px solid #334155", borderRadius: 12 };

export const OutcomeTimeline = ({ rows }) => {
  const shown = rows.filter((o) => o.timeline?.length).slice(0, 8);
  if (!shown.length) return null;
  const points = {};
  shown.forEach((o, i) => o.timeline.forEach((t, j) => {
    const k = `${t.at}-${j}`;
    points[k] = { ...(points[k] || { at: t.at, label: day(t.at) }), [`o${i}`]: t.pct };
  }));
  const data = Object.values(points).sort((a, b) => (a.at > b.at ? 1 : -1));
  return (
    <div className="panel p-4 space-y-2" data-testid="outcome-timeline">
      <p className="eyebrow">Çıktı gelişimi · doğru yüzdesinin zamana göre değişimi</p>
      <div className="h-72">
        <ResponsiveContainer>
          <LineChart data={data}>
            <CartesianGrid stroke="rgba(255,255,255,0.06)" />
            <XAxis dataKey="label" stroke="#94A3B8" fontSize={12} />
            <YAxis domain={[0, 100]} stroke="#94A3B8" fontSize={12} unit="%" />
            <Tooltip contentStyle={tip} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {shown.map((o, i) => <Line key={o.id} type="monotone" dataKey={`o${i}`} name={o.text.slice(0, 40)} stroke={LINE_COLORS[i]} strokeWidth={2.5} dot connectNulls />)}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const ClassEditor = ({ c, onSaved }) => {
  const [name, setName] = useState(c?.name || "");
  const [list, setList] = useState((c?.students || []).join("\n"));
  const save = async () => {
    const body = { name, students: list.split("\n").map((s) => s.trim()).filter(Boolean) };
    try { if (c) await api.put(`/classes/${c.id}`, body); else { await api.post("/classes", body); setName(""); setList(""); } toast.success("Sınıf kaydedildi"); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="panel p-4 space-y-2" data-testid={`class-editor-${c?.id || "new"}`}>
      <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Sınıf adı (örn. 9-A)" data-testid={`class-name-${c?.id || "new"}`} />
      <Textarea value={list} onChange={(e) => setList(e.target.value)} rows={4} placeholder="Her satıra bir öğrenci adı soyadı" data-testid={`class-students-${c?.id || "new"}`} />
      <div className="flex gap-2">
        <Button size="sm" onClick={save} disabled={!name.trim()} data-testid={`class-save-${c?.id || "new"}`}>{c ? "Kaydet" : <><Plus className="h-4 w-4 mr-1" />Sınıf oluştur</>}</Button>
        {c && <Button size="sm" variant="ghost" className="text-red-400" onClick={() => api.delete(`/classes/${c.id}`).then(onSaved)} data-testid={`class-delete-${c.id}`}><Trash2 className="h-4 w-4" /></Button>}
      </div>
    </div>
  );
};

const Progress = ({ p }) => (
  <div className="space-y-4" data-testid="class-progress">
    {p.games.length === 0 ? <p className="text-sm text-slate-400">Bu sınıftaki öğrencilerin katıldığı oyun kaydı yok.</p> : (
      <div className="panel p-4 h-72">
        <ResponsiveContainer>
          <LineChart data={p.games.map((g) => ({ label: `${day(g.at)} · ${g.code}`, pct: g.class_pct }))}>
            <CartesianGrid stroke="rgba(255,255,255,0.06)" />
            <XAxis dataKey="label" stroke="#94A3B8" fontSize={11} />
            <YAxis domain={[0, 100]} stroke="#94A3B8" fontSize={12} unit="%" />
            <Tooltip contentStyle={tip} />
            <Line type="monotone" dataKey="pct" name="Sınıf başarısı" stroke="#22C55E" strokeWidth={3} dot />
          </LineChart>
        </ResponsiveContainer>
      </div>
    )}
    <div className="panel p-4 overflow-x-auto">
      <table className="w-full text-sm" data-testid="class-students-table">
        <thead className="text-slate-400 text-left"><tr><th className="py-2">Öğrenci</th><th>Durum</th><th>Oyun</th><th>Ortalama</th><th>İlk</th><th>Son</th><th>Gelişim</th></tr></thead>
        <tbody>
          {p.students.map((s) => {
            const d = s.first != null && s.last != null ? s.last - s.first : null;
            return (
              <tr key={s.name} className="border-t border-slate-700/40">
                <td className="py-2 font-semibold">{s.name}</td>
                <td data-testid={`class-student-status-${s.name}`}><span className={`inline-block h-2.5 w-2.5 rounded-full mr-1.5 ${s.online ? "bg-[#22C55E]" : "bg-slate-600"}`} />{s.online ? "Çevrim içi" : s.last_seen ? new Date(s.last_seen).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" }) : "—"}</td><td>{s.games}</td><td>{s.avg != null ? `%${s.avg}` : "—"}</td>
                <td>{s.first != null ? `%${s.first}` : "—"}</td><td>{s.last != null ? `%${s.last}` : "—"}</td>
                <td className={d > 0 ? "text-[#4ADE80]" : d < 0 ? "text-red-400" : "text-slate-400"}>{d == null ? "—" : `${d > 0 ? "+" : ""}${d}`}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </div>
);

export const ClassesSection = () => {
  const [classes, setClasses] = useState([]);
  const [sel, setSel] = useState(null);
  const [prog, setProg] = useState(null);
  const load = () => api.get("/classes").then((r) => setClasses(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!sel) { setProg(null); return; }
    api.get(`/classes/${sel}/progress`).then((r) => setProg(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [sel]);
  return (
    <section className="space-y-4" data-testid="classes-section">
      <h2 className="font-display text-2xl font-bold flex items-center gap-2"><Users className="h-6 w-6 text-[#FACC15]" />Sınıflarım</h2>
      <p className="text-sm text-slate-400">Öğrenci adlarını ekleyin ya da oyun oluştururken sınıf seçin; katılan öğrenciler otomatik eklenir. Eşleşmede büyük/küçük harf ve Türkçe karakter farkı önemsenmez.</p>
      <div className="flex flex-wrap gap-2">
        {classes.map((c) => <Button key={c.id} size="sm" variant={sel === c.id ? "default" : "outline"} onClick={() => setSel(sel === c.id ? null : c.id)} data-testid={`class-tab-${c.id}`}>{c.name} · {c.students.length}</Button>)}
      </div>
      <div className="grid lg:grid-cols-3 gap-3">
        {classes.filter((c) => c.id === sel).map((c) => <ClassEditor key={`${c.id}-${c.students.length}`} c={c} onSaved={() => { load(); setSel(null); }} />)}
        <ClassEditor key={`new-${classes.length}`} onSaved={load} />
      </div>
      {prog && <Progress p={prog} />}
    </section>
  );
};
